import React, { useEffect, useState, useCallback } from "react";
import { ArrowLeft, Pencil, Trash2, Phone, Smartphone, User2, Calendar, FileText, Store, History, Users, RefreshCw, Link2 } from "lucide-react";
import { Button } from "./ui/button";
import StatusBadge from "./StatusBadge";
import { formatDateDMY } from "../lib/format";
import { DEFAULT_STORE, jobsApi, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import LinkCustomerDialog from "./LinkCustomerDialog";

const Row = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-4 py-3.5 border-b border-slate-100 last:border-b-0">
    <Icon className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
      <p data-testid={testid} className="text-[15px] text-slate-900 break-words mt-1 leading-snug">
        {value || "—"}
      </p>
    </div>
  </div>
);

const ACTION_LABEL = {
  created: "Skapade ärendet",
  updated: "Uppdaterade",
  status_changed: "Ändrade status",
  trashed: "Flyttade till papperskorgen",
  restored: "Återställde från papperskorgen",
  deleted_permanently: "Raderade permanent",
  customer_linked: "Ändrade kundkoppling",
};

const FIELD_LABEL = {
  status: "Status", store: "Butik", technician: "Tekniker",
  customerName: "Kundnamn", phone: "Telefon", device: "Enhet",
  supplier: "Leverantör", date: "Datum", notes: "Anteckningar",
  customerId: "Kundkoppling",
};

function formatStockholm(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      timeZone: "Europe/Stockholm",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export const JobDetail = ({ job: initialJob, onBack, onEdit, onDelete, refreshToken = 0 }) => {
  const [job, setJob] = useState(initialJob);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState("");
  const [customerJobs, setCustomerJobs] = useState(null);
  const [customerErr, setCustomerErr] = useState("");
  const [showAllCustomerJobs, setShowAllCustomerJobs] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const { isAdmin } = useAuth();

  const refreshJob = useCallback(async () => {
    if (!initialJob?.id) return;
    try {
      const fresh = await jobsApi.get(initialJob.id);
      setJob(fresh);
    } catch (e) { /* keep prev */ }
  }, [initialJob?.id]);

  const loadHistory = useCallback(async () => {
    if (!initialJob?.id) return;
    setHistoryError("");
    try {
      const h = await jobsApi.history(initialJob.id);
      setHistory(h);
    } catch (e) {
      setHistoryError(formatApiError(e));
    }
  }, [initialJob?.id]);

  const loadCustomerJobs = useCallback(async () => {
    if (!job?.customerId) {
      setCustomerJobs([]);
      return;
    }
    setCustomerErr("");
    try {
      const list = await jobsApi.listForCustomer(job.customerId, job.id);
      setCustomerJobs(list);
    } catch (e) { setCustomerErr(formatApiError(e)); }
  }, [job?.customerId, job?.id]);

  useEffect(() => { setJob(initialJob); }, [initialJob]);

  // Auto-refresh job + history + customer jobs on mount and when refreshToken bumps
  useEffect(() => {
    refreshJob();
    loadHistory();
    loadCustomerJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJob?.id, refreshToken]);

  // Re-run customer jobs when customerId becomes known
  useEffect(() => { loadCustomerJobs(); }, [job?.customerId, loadCustomerJobs]);

  if (!job) return null;
  const createdPretty = formatStockholm(job.createdAt);
  const visibleCustomerJobs = (customerJobs || []).slice(0, showAllCustomerJobs ? 100 : 5);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 fade-in-up">
      <button data-testid="detail-back-btn" onClick={onBack}
              className="press-feedback inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#3B9EDA] mb-5 min-h-[44px] -ml-2 pl-2 pr-3 rounded-full transition">
        <ArrowLeft className="h-4 w-4" /> Tillbaka
      </button>

      <div className="soft-card p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-slate-500 mb-1.5">Kund</p>
            <h1 data-testid="detail-customer-name"
                className="text-3xl sm:text-4xl font-bold display-tight"
                style={{ color: "#0F172A", fontFamily: "Manrope, sans-serif" }}>
              {job.customerName}
            </h1>
            {!job.customerId && (
              <p data-testid="no-customer-link" className="text-xs text-amber-700 mt-2 inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Kunden är inte kopplad till ett kundkort.
              </p>
            )}
            {isAdmin && (
              <button
                data-testid="link-customer-btn"
                type="button"
                onClick={() => setLinkOpen(true)}
                className="press-feedback inline-flex items-center gap-1 text-xs text-[#3B9EDA] hover:underline mt-2 min-h-[32px] rounded-full px-1">
                <Link2 className="h-3.5 w-3.5" />
                {job.customerId ? "Byt kundkoppling" : "Koppla till kund"}
              </button>
            )}
          </div>
          <StatusBadge status={job.status} className="text-sm px-4 py-1.5" />
        </div>

        <div className="divide-y divide-slate-100">
          <Row icon={Smartphone} label="Enhet / Modell" value={job.device} testid="detail-device" />
          <Row icon={Phone} label="Telefon" value={job.phone || "Telefonnummer saknas"} testid="detail-phone" />
          <Row icon={User2} label="Ansvarig tekniker" value={job.technician} testid="detail-technician" />
          <Row icon={Store} label="Butik" value={job.store || DEFAULT_STORE} testid="detail-store" />
          <Row icon={Calendar} label="Datum" value={formatDateDMY(job.date)} testid="detail-date" />
          <Row icon={FileText} label="Anteckningar" value={job.notes} testid="detail-notes" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Button data-testid="detail-edit-btn" onClick={() => onEdit(job)}
                  className="press-feedback rounded-full px-6 min-h-[44px] text-white shadow-sm" style={{ backgroundColor: "#3B9EDA" }}>
            <Pencil className="h-4 w-4 mr-2" /> Redigera
          </Button>
          <Button data-testid="detail-delete-btn" onClick={() => onDelete(job)}
                  variant="outline" className="press-feedback rounded-full px-6 min-h-[44px] border-red-200 text-red-600 hover:bg-red-50">
            <Trash2 className="h-4 w-4 mr-2" /> Flytta till papperskorgen
          </Button>
        </div>
      </div>

      {/* Ändringshistorik */}
      <div className="soft-card p-6 sm:p-8 mt-6" data-testid="history-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold display-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <History className="h-5 w-5 text-slate-400" />
            Ändringshistorik
          </h2>
          <button onClick={loadHistory} data-testid="history-refresh"
                  className="press-feedback text-xs text-slate-500 hover:text-[#3B9EDA] inline-flex items-center gap-1 min-h-[36px] px-3 rounded-full">
            <RefreshCw className="h-3.5 w-3.5" /> Uppdatera
          </button>
        </div>

        {historyError ? (
          <div data-testid="history-error" className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
            Kunde inte ladda historik: {historyError}
            <button onClick={loadHistory} className="ml-2 underline font-medium">Försök igen</button>
          </div>
        ) : history === null ? (
          <p className="text-sm text-slate-400">Laddar historik…</p>
        ) : history.length === 0 ? (
          <p data-testid="history-empty" className="text-sm text-slate-500 italic">
            Historik saknas före införandet.
            <br />
            <span className="text-xs text-slate-400">Ärendet skapades {createdPretty}
              {job.createdBy ? ` av ${job.createdBy}` : " (okänd skapare)"}.
            </span>
          </p>
        ) : (
          <ol className="relative border-l-2 border-slate-100 pl-5 space-y-5">
            {history.map((h, i) => {
              const changes = Object.entries(h.changes || {});
              // Legacy history records may use `by` instead of byDisplayName/byUsername.
              // Preserve historical attribution without rewriting old records.
              const displayName = h.byDisplayName || h.byUsername || h.by || "okänd";
              return (
                <li key={i} data-testid={`history-item-${i}`} className="relative">
                  <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full bg-[#3B9EDA] ring-4 ring-white" />
                  <div className="text-sm">
                    <span className="font-semibold text-slate-800">{displayName}</span>
                    <span className="text-slate-500"> · {formatStockholm(h.at)}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    {ACTION_LABEL[h.action] || h.action}
                  </div>
                  {changes.length > 0 && (
                    <ul className="mt-1 ml-1 text-xs text-slate-500 space-y-0.5">
                      {changes.map(([k, v]) => (
                        <li key={k}>
                          <span className="font-medium">{FIELD_LABEL[k] || k}:</span>{" "}
                          <span className="line-through text-slate-400">{String(v[0] ?? "—")}</span>{" "}
                          → <span className="text-slate-700">{String(v[1] ?? "—")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Kundhistorik */}
      <div className="soft-card p-6 sm:p-8 mt-6" data-testid="customer-history-section">
        <h2 className="flex items-center gap-2 text-lg font-semibold display-tight text-slate-900 mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          <Users className="h-5 w-5 text-slate-400" />
          Kundhistorik
        </h2>

        {!job.customerId ? (
          <p data-testid="customer-history-unlinked" className="text-sm text-slate-500 italic">
            Kunden är inte kopplad till ett kundkort — ingen kundhistorik kan visas.
          </p>
        ) : customerErr ? (
          <div className="text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
            {customerErr}
            <button onClick={loadCustomerJobs} className="ml-2 underline font-medium">Försök igen</button>
          </div>
        ) : customerJobs === null ? (
          <p className="text-sm text-slate-400">Laddar kundhistorik…</p>
        ) : customerJobs.length === 0 ? (
          <p data-testid="customer-history-empty" className="text-sm text-slate-500 italic">
            Inga tidigare ärenden för den här kunden.
          </p>
        ) : (
          <>
            <ul className="divide-y divide-slate-100" data-testid="customer-history-list">
              {visibleCustomerJobs.map((cj) => (
                <li key={cj.id} data-testid={`customer-job-${cj.id}`}
                    className="py-3 flex items-start justify-between gap-3 hover:bg-slate-50 rounded px-2 -mx-2 cursor-pointer"
                    onClick={() => window.location.href = `/arenden?open=${cj.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs text-slate-400">{formatDateDMY(cj.date)}</span>
                      <StatusBadge status={cj.status} />
                    </div>
                    <div className="text-sm font-medium text-slate-800 truncate">{cj.device}</div>
                    {cj.notes && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">{cj.notes}</div>
                    )}
                    <div className="text-xs text-slate-400 mt-0.5">
                      {cj.store || DEFAULT_STORE} · Ansvarig: {cj.technician || "—"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {customerJobs.length > 5 && !showAllCustomerJobs && (
              <button data-testid="show-more-customer-jobs"
                      onClick={() => setShowAllCustomerJobs(true)}
                      className="mt-3 text-sm text-[#3B9EDA] hover:underline font-medium">
                Visa fler ({customerJobs.length - 5} till)
              </button>
            )}
          </>
        )}
      </div>

      <LinkCustomerDialog
        open={linkOpen}
        job={job}
        onClose={() => setLinkOpen(false)}
        onLinked={() => { refreshJob(); loadHistory(); loadCustomerJobs(); }}
      />
    </div>
  );
};

export default JobDetail;
